import { Otp } from '@repo/shared/components'
import { useTranslate } from '@repo/shared/hooks'
import { useRouter } from '@repo/shared/i18n/navigation'
import {
  useAgentLoginByPhone,
  useAgentLoginByEmail,
  useAgentResendOtp,
  useAgentAuthDetails,
} from '@repo/services'
import { Button, Card, Col, Icon, toast } from '@repo/ui/components'
import { useRef, useState } from 'react'
import { IForgotPasswordOtpProps } from './forgot-password.types'
import { normalizeMobile } from '@repo/ui/lib'
import { OTP_LENGTH } from '@repo/shared/variables/otp.var'
import { useErrorHandler } from '@repo/shared/hooks'
import { Cookie } from '@repo/tools'

export const ForgotPasswordOtp = (props: IForgotPasswordOtpProps) => {
  const { identifier, isEmail, otp, handleBack } = props
  const { t } = useTranslate()
  const { replace } = useRouter()
  const [otpCode, setOtpCode] = useState<string>('')
  const [isLoading, setIsLoading] = useState(false)
  const loginByPhone = useAgentLoginByPhone()
  const loginByEmail = useAgentLoginByEmail()
  const resendOtpMutation = useAgentResendOtp()
  const authDetailsQuery = useAgentAuthDetails()
  const { errorHandler } = useErrorHandler()
  const submitRef = useRef(null)

  const getResendOtpPayload = () =>
    isEmail
      ? { email: identifier, otp }
      : { phone: normalizeMobile(identifier), otp }

  const handleResend = () => {
    resendOtpMutation.mutate(getResendOtpPayload(), {
      onSuccess: () => {
        toast.success({
          title: t('common_otp_resend'),
        })
      },
      onError: errorHandler,
    })
  }

  const handleOtpComplete = (code: string) => {
    setOtpCode(code)
  }

  const handleAuthDetailsAndRedirect = async (redirectUrl: string) => {
    try {
      const authDetails = await authDetailsQuery.refetch()
      if (authDetails.data?.data?.details?.kyc?.status) {
        Cookie.set(
          'kyc_status',
          authDetails.data.data.details.kyc.status || 'undefined',
          {
            path: '/',
          },
        )
        Cookie.set(
          'kyc_reason',
          authDetails.data.data.details.kyc.comment || 'undefined',
          {
            path: '/',
          },
        )
      }
      replace(redirectUrl)
    } catch {
      // If auth details fails, still redirect
      replace(redirectUrl)
    }
  }

  const handleSubmit = async (code: string = otpCode) => {
    if (!code || code.length < OTP_LENGTH) return

    setIsLoading(true)

    if (isEmail) {
      loginByEmail.mutate(
        {
          email: identifier,
          otp,
          code,
        },
        {
          onSuccess: (res) => {
            if (res.data?.accessToken && res.data?.refreshToken) {
              // Get auth details and set KYC status in cookie
              handleAuthDetailsAndRedirect('/auth/change-password')
            }
          },
          onError: (error) => {
            setIsLoading(false)
            errorHandler(error, t)
          },
        },
      )
    } else {
      loginByPhone.mutate(
        {
          phone: normalizeMobile(identifier),
          otp,
          code,
        },
        {
          onSuccess: (res) => {
            if (res.data?.accessToken && res.data?.refreshToken) {
              // Get auth details and set KYC status in cookie
              handleAuthDetailsAndRedirect('/auth/change-password')
            }
          },
          onError: (error) => {
            setIsLoading(false)
            errorHandler(error, t)
          },
        },
      )
    }
  }

  return (
    <Card className='p-xl rounded-sm'>
      <Col className='gap-3xl'>
        <Button
          startIcon={<Icon name='ArrowRightLine' />}
          variant='transparent'
          size='xs'
          onClick={handleBack}
          className='gap-2xs'
        >
          {t('common_back')}
        </Button>
        <Otp
          mobileOrEmail={identifier}
          onResend={handleResend}
          onComplete={handleOtpComplete}
          focusRef={submitRef}
          resendLoading={resendOtpMutation.isPending}
          resendError={resendOtpMutation.isError}
        />
        <Button
          ref={submitRef}
          size='lg'
          className='w-full'
          onClick={() => handleSubmit()}
          disabled={otpCode.length < OTP_LENGTH}
          loading={
            isLoading ||
            (isEmail ? loginByEmail.isPending : loginByPhone.isPending)
          }
        >
          {t('common_login')}
        </Button>
      </Col>
    </Card>
  )
}
